/**
 * Groups messages into time buckets based on a specified interval
 * @param {Array} messages - Array of message objects with timestamp property
 * @param {number} intervalMinutes - Size of each time bucket in minutes
 * @returns {Object} - Messages grouped by bucket timestamps
 */
export function bucketMessagesByTimestamp<
  TMessage extends { timestamp: string | Date },
>(messages: TMessage[], intervalMinutes = 4) {
  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  )

  const buckets: Record<string, TMessage[]> = {}

  const intervalMs = intervalMinutes * 60 * 1000 // Convert minutes to milliseconds

  sortedMessages.forEach((message) => {
    const timestamp = new Date(message.timestamp).getTime()

    // Find or create appropriate bucket
    let bucketFound = false

    for (const bucketKey in buckets) {
      const bucketStartTime = new Date(bucketKey).getTime()
      const bucketEndTime = bucketStartTime + intervalMs

      // If message falls within an existing bucket's time range
      if (timestamp >= bucketStartTime && timestamp < bucketEndTime) {
        // @ts-ignore - we know this bucket exists because we're
        // iterating over all buckets
        buckets[bucketKey].push(message)
        bucketFound = true
        break
      }
    }

    // If no suitable bucket was found, create a new one
    if (!bucketFound) {
      const newBucketKey = new Date(timestamp).toISOString()
      buckets[newBucketKey] = [message]
    }
  })

  return Object.entries(buckets).map(([timestamp, messages]) => ({
    timestamp,
    messages,
  })) as { timestamp: string; messages: TMessage[] }[]
}
